import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { ArrowLeft, Mail, Phone, MapPin, Calendar, MessageSquare, Plus, Trash2, Send, Loader2, CheckCircle2, DollarSign, Wrench } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/pages/admin/AdminLayout";
import { fetchQuote, fetchConsultation, sendEstimate, absoluteUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const labelClass = "font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/50";
const fieldClass = "bg-[#05070A] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[color:var(--primary)] focus-visible:ring-offset-0 rounded-none";
const TIER_ACCENTS = ["text-white/70 border-white/25", "text-[color:var(--primary)] border-[color:var(--primary)]/60", "text-amber-300 border-amber-400/50"];
const TIER_BAR = ["bg-white/25", "bg-[color:var(--primary)]", "bg-amber-300"];
const TIER_LABELS = ["Essential", "Signature", "Ultimate"];

function InfoRow({ Icon, label, value, href }) {
  const content = (
    <div className="flex items-start gap-3">
      <Icon size={16} className="text-[color:var(--primary)] mt-0.5 flex-shrink-0" />
      <div>
        <div className={labelClass}>{label}</div>
        <div className="text-white text-sm md:text-base mt-0.5 break-all">{value || "—"}</div>
      </div>
    </div>
  );
  return href ? <a href={href} className="hover:opacity-80 transition-opacity">{content}</a> : content;
}

const defaultOptions = () => ([
  {
    name: "Essential",
    description: "Core install for a clean, factory-spec finish. Everything you need, nothing you don't.",
    line_items: [{ description: "Omni RGB perimeter install", quantity: 100, unit_price: 30 }],
    tax_percent: 5.5,
    discount: 0,
  },
  {
    name: "Signature",
    description: "Our most popular package — extended perimeter, uplighting accents, priority support.",
    line_items: [
      { description: "Omni RGB perimeter (extended)", quantity: 150, unit_price: 35 },
      { description: "Landscape uplighting (6 fixtures)", quantity: 6, unit_price: 180 },
    ],
    tax_percent: 5.5,
    discount: 0,
  },
  {
    name: "Ultimate",
    description: "Full property spec: perimeter, landscape, tree wraps, and premium programming for every season.",
    line_items: [
      { description: "Omni RGB full perimeter", quantity: 200, unit_price: 38 },
      { description: "Landscape uplighting (12 fixtures)", quantity: 12, unit_price: 180 },
      { description: "Tree wraps + water feature accents", quantity: 1, unit_price: 1200 },
      { description: "Premium programming + 5-year warranty", quantity: 1, unit_price: 800 },
    ],
    tax_percent: 5.5,
    discount: 0,
  },
]);

const defaultMaintenance = () => ({
  enabled: false,
  name: "Omni Maintenance Contract",
  visits_per_year: 2,
  price_per_year: 350,
  description: "Two annual tune-ups, firmware updates, and priority support. Keep your Omni system flawless year-round.",
});

function computeOption(opt) {
  const sub = opt.line_items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
  const net = Math.max(0, sub - (Number(opt.discount) || 0));
  const taxAmt = net * ((Number(opt.tax_percent) || 0) / 100);
  return { subtotal: sub, tax_amount: taxAmt, total: net + taxAmt };
}

function OptionCard({ opt, index, onChange, onRemove, canRemove }) {
  const totals = computeOption(opt);
  const accent = TIER_ACCENTS[index] || "text-white/60 border-white/15";
  const bar = TIER_BAR[index] || "bg-white/30";
  const tierLabel = TIER_LABELS[index] || `Option ${index + 1}`;

  const updateLine = (i, field, value) => {
    const nl = opt.line_items.map((r, x) => x === i ? { ...r, [field]: value } : r);
    onChange({ ...opt, line_items: nl });
  };
  const addLine = () => onChange({ ...opt, line_items: [...opt.line_items, { description: "", quantity: 1, unit_price: 0 }] });
  const removeLine = (i) => onChange({ ...opt, line_items: opt.line_items.filter((_, x) => x !== i) });

  return (
    <div className="border border-white/10 bg-[#0C1017]" data-testid={`estimate-option-${index}`}>
      <div className={`h-1 w-full ${bar}`} />
      <div className="p-5 md:p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex-1">
            <div className={`inline-block font-mono-accent text-[9px] tracking-[0.3em] uppercase px-2 py-1 border ${accent} mb-2`}>
              Option {index + 1} · {tierLabel}
            </div>
            <Input
              value={opt.name}
              onChange={(e) => onChange({ ...opt, name: e.target.value })}
              placeholder={`${tierLabel} Package`}
              className={fieldClass + " h-11 text-lg font-semibold"}
              data-testid={`option-${index}-name`}
            />
          </div>
          {canRemove && (
            <button
              onClick={onRemove}
              className="mt-9 text-white/40 hover:text-red-400"
              aria-label="Remove option"
              data-testid={`option-${index}-remove`}
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>

        <div className="space-y-2 mb-4">
          <Label className={labelClass}>Description of work</Label>
          <Textarea
            value={opt.description || ""}
            onChange={(e) => onChange({ ...opt, description: e.target.value })}
            rows={3}
            placeholder="What's included in this option? (visible in the estimate email)"
            className={fieldClass}
            data-testid={`option-${index}-description`}
          />
        </div>

        {/* Line items */}
        <div className="space-y-2">
          <div className="grid grid-cols-[1fr_70px_100px_100px_32px] gap-2 pb-1 border-b border-white/10">
            <div className={labelClass}>Item</div>
            <div className={labelClass + " text-center"}>Qty</div>
            <div className={labelClass + " text-right"}>Unit</div>
            <div className={labelClass + " text-right"}>Sub</div>
            <div />
          </div>
          {opt.line_items.map((it, i) => {
            const sub = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
            return (
              <div key={i} className="grid grid-cols-[1fr_70px_100px_100px_32px] gap-2 items-center" data-testid={`option-${index}-line-${i}`}>
                <Input
                  value={it.description}
                  onChange={(e) => updateLine(i, "description", e.target.value)}
                  placeholder="Line description"
                  className={fieldClass + " h-9 text-sm"}
                  data-testid={`option-${index}-line-${i}-desc`}
                />
                <Input
                  type="number" min="0" step="0.5"
                  value={it.quantity}
                  onChange={(e) => updateLine(i, "quantity", e.target.value)}
                  className={fieldClass + " h-9 text-sm text-center"}
                  data-testid={`option-${index}-line-${i}-qty`}
                />
                <Input
                  type="number" min="0" step="0.01"
                  value={it.unit_price}
                  onChange={(e) => updateLine(i, "unit_price", e.target.value)}
                  className={fieldClass + " h-9 text-sm text-right"}
                  data-testid={`option-${index}-line-${i}-unit`}
                />
                <div className="text-right text-white text-sm px-1">${sub.toFixed(2)}</div>
                <button
                  onClick={() => removeLine(i)}
                  disabled={opt.line_items.length === 1}
                  className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-red-400 disabled:opacity-30"
                  aria-label="Remove line"
                  data-testid={`option-${index}-line-${i}-remove`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            );
          })}
          <button
            onClick={addLine}
            className="inline-flex items-center gap-1 font-mono-accent text-[10px] tracking-[0.25em] uppercase text-[color:var(--primary)] hover:text-white transition-colors mt-1"
            data-testid={`option-${index}-add-line`}
          >
            <Plus size={12} /> Add item
          </button>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className={labelClass}>Tax %</Label>
            <Input
              type="number" min="0" step="0.01"
              value={opt.tax_percent}
              onChange={(e) => onChange({ ...opt, tax_percent: e.target.value })}
              className={fieldClass + " h-9 text-sm"}
              data-testid={`option-${index}-tax`}
            />
          </div>
          <div className="space-y-1">
            <Label className={labelClass}>Discount $</Label>
            <Input
              type="number" min="0" step="0.01"
              value={opt.discount}
              onChange={(e) => onChange({ ...opt, discount: e.target.value })}
              className={fieldClass + " h-9 text-sm"}
              data-testid={`option-${index}-discount`}
            />
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-white/10 space-y-1 text-sm">
          <div className="flex justify-between text-white/60"><span>Subtotal</span><span className="text-white">${totals.subtotal.toFixed(2)}</span></div>
          {Number(opt.tax_percent) > 0 && (
            <div className="flex justify-between text-white/60"><span>Tax</span><span className="text-white">${totals.tax_amount.toFixed(2)}</span></div>
          )}
          <div className="flex items-baseline justify-between pt-2 mt-1 border-t border-white/10">
            <span className="font-mono-accent text-[10px] tracking-[0.28em] uppercase text-white/60">Total</span>
            <span className="font-display uppercase text-2xl md:text-3xl text-white tracking-tight" data-testid={`option-${index}-total`}>${totals.total.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RequestDetail({ type = "quote" }) {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [options, setOptions] = useState(defaultOptions());
  const [maintenance, setMaintenance] = useState(defaultMaintenance());
  const [validDays, setValidDays] = useState(30);
  const [msg, setMsg] = useState("Thanks for reaching out! Below are three tiered options for your project. Reply to this email if you'd like to move forward or need adjustments.");

  useEffect(() => {
    let m = true;
    (async () => {
      setLoading(true);
      try {
        const d = type === "quote" ? await fetchQuote(id) : await fetchConsultation(id);
        if (!m) return;
        setData(d);
      } catch (e) {
        toast.error("Could not load request.");
        nav(type === "quote" ? "/admin" : "/admin/consultations");
      } finally {
        m && setLoading(false);
      }
    })();
    return () => { m = false; };
  }, [id, type, nav]);

  const updateOption = (i, next) => setOptions((prev) => prev.map((o, x) => x === i ? next : o));
  const removeOption = (i) => setOptions((prev) => prev.filter((_, x) => x !== i));
  const addOption = () => {
    if (options.length >= 5) return;
    setOptions((prev) => [...prev, {
      name: `Option ${prev.length + 1}`,
      description: "",
      line_items: [{ description: "", quantity: 1, unit_price: 0 }],
      tax_percent: 5.5,
      discount: 0,
    }]);
  };

  const onSend = async () => {
    if (options.length === 0) {
      toast.error("Please add at least one quote option.");
      return;
    }
    for (const [i, o] of options.entries()) {
      if (!o.name?.trim()) return toast.error(`Option ${i + 1} needs a name.`);
      for (const [li, l] of o.line_items.entries()) {
        if (!l.description?.trim()) return toast.error(`Option ${i + 1}, line ${li + 1} needs a description.`);
        if (Number(l.quantity) <= 0) return toast.error(`Option ${i + 1}, line ${li + 1} needs a positive quantity.`);
      }
    }
    if (maintenance.enabled) {
      if (!(Number(maintenance.visits_per_year) > 0)) return toast.error("Maintenance visits/year must be greater than 0.");
      if (!(Number(maintenance.price_per_year) >= 0)) return toast.error("Maintenance price/year is invalid.");
    }

    setSending(true);
    try {
      const payload = {
        options: options.map((o) => ({
          name: o.name.trim(),
          description: o.description?.trim() || null,
          line_items: o.line_items.map((l) => ({
            description: l.description,
            quantity: Number(l.quantity) || 0,
            unit_price: Number(l.unit_price) || 0,
          })),
          tax_percent: Number(o.tax_percent) || 0,
          discount: Number(o.discount) || 0,
        })),
        maintenance: maintenance.enabled ? {
          enabled: true,
          name: maintenance.name?.trim() || "Omni Maintenance Contract",
          visits_per_year: Number(maintenance.visits_per_year) || 0,
          price_per_year: Number(maintenance.price_per_year) || 0,
          description: maintenance.description?.trim() || null,
        } : null,
        personal_message: msg?.trim() || null,
        valid_days: Number(validDays) || 30,
      };
      await sendEstimate(type, id, payload);
      toast.success(`Estimate emailed to ${data.email}.`);
      const d = type === "quote" ? await fetchQuote(id) : await fetchConsultation(id);
      setData(d);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to send estimate.");
    } finally {
      setSending(false);
    }
  };

  if (loading || !data) {
    return (
      <AdminLayout>
        <div className="min-h-[40vh] flex items-center justify-center text-white/50">
          <Loader2 className="animate-spin mr-2" size={16} /> Loading request…
        </div>
      </AdminLayout>
    );
  }

  const backTo = type === "quote" ? "/admin" : "/admin/consultations";
  const created = data.created_at ? new Date(data.created_at) : null;
  const estimates = data.estimates || [];
  const perVisit = Number(maintenance.visits_per_year) > 0 ? Number(maintenance.price_per_year) / Number(maintenance.visits_per_year) : 0;

  return (
    <AdminLayout>
      <Link to={backTo} className="inline-flex items-center gap-2 font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/50 hover:text-[color:var(--primary)] mb-8">
        <ArrowLeft size={12} /> Back to {type === "quote" ? "Quotes" : "Consultations"}
      </Link>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div>
          <div className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-[color:var(--primary)] mb-2">
            {type === "quote" ? "Quote Request" : "Consultation Booking"} · #{data.id.slice(0, 8).toUpperCase()}
          </div>
          <h1 className="font-display uppercase text-white text-4xl md:text-6xl tracking-tight leading-none">
            {data.name}
          </h1>
          {created && (
            <div className="mt-2 text-white/40 text-sm">Received {format(created, "PPP · p")}</div>
          )}
        </div>
        {estimates.length > 0 && (
          <div className="inline-flex items-center gap-2 px-3 py-2 border border-emerald-500/40 bg-emerald-500/5 text-emerald-300 font-mono-accent text-[10px] tracking-[0.3em] uppercase self-start">
            <CheckCircle2 size={12} /> Estimate sent · {estimates.length}
          </div>
        )}
      </div>

      <div className="grid xl:grid-cols-[380px_1fr] gap-6">
        {/* Left column */}
        <div className="space-y-6">
          <div className="border border-white/10 bg-[#0C1017] p-6">
            <div className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/40 mb-5">Customer</div>
            <div className="space-y-5">
              <InfoRow Icon={Mail} label="Email" value={data.email} href={`mailto:${data.email}`} />
              <InfoRow Icon={Phone} label="Phone" value={data.phone} href={`tel:${data.phone}`} />
              {type === "quote" ? (
                <>
                  <InfoRow Icon={DollarSign} label="Service" value={data.service} />
                  <InfoRow Icon={MapPin} label="Address" value={data.address} />
                  <InfoRow Icon={MessageSquare} label="Property type" value={data.property_type} />
                  {data.message && (
                    <div>
                      <div className={labelClass}>Message</div>
                      <div className="text-white/80 text-sm mt-1 whitespace-pre-line leading-relaxed">{data.message}</div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <InfoRow Icon={Calendar} label="Preferred" value={`${data.preferred_date}${data.preferred_time ? ` · ${data.preferred_time}` : ""}`} />
                  <InfoRow Icon={DollarSign} label="Interest" value={data.service_interest} />
                  {data.notes && (
                    <div>
                      <div className={labelClass}>Notes</div>
                      <div className="text-white/80 text-sm mt-1 whitespace-pre-line leading-relaxed">{data.notes}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {data.photos && data.photos.length > 0 && (
            <div className="border border-white/10 bg-[#0C1017] p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/40">Photos</div>
                <div className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-white/40">{data.photos.length}</div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {data.photos.map((p, i) => (
                  <button key={i} onClick={() => setLightbox(p)} className="aspect-square overflow-hidden border border-white/10 hover:border-[color:var(--primary)]/60 transition-colors" data-testid={`admin-photo-${i}`}>
                    <img src={absoluteUrl(p)} alt="attachment" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {estimates.length > 0 && (
            <div className="border border-white/10 bg-[#0C1017] p-6">
              <div className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/40 mb-4">Estimates sent</div>
              <div className="space-y-3">
                {estimates.map((e) => {
                  const totals = (e.options || []).map((o) => o.total || 0);
                  const lo = totals.length ? Math.min(...totals) : 0;
                  const hi = totals.length ? Math.max(...totals) : 0;
                  return (
                    <div key={e.id} className="border-l-2 border-[color:var(--primary)] pl-3">
                      <div className="text-white text-lg">
                        {lo === hi ? `$${lo.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : `$${lo.toLocaleString(undefined, { minimumFractionDigits: 0 })} — $${hi.toLocaleString(undefined, { minimumFractionDigits: 0 })}`}
                      </div>
                      <div className="text-white/50 text-xs mt-0.5">
                        {format(new Date(e.sent_at), "MMM d, yyyy · h:mm a")} · {(e.options || []).length} tier{(e.options || []).length > 1 ? "s" : ""}{e.maintenance?.enabled ? " · +maintenance" : ""}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Right: Estimate builder */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="space-y-6">
          <div className="border border-white/10 bg-[#0C1017] p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-[color:var(--primary)] mb-1">Pricing Estimate · Tiered</div>
                <div className="font-display uppercase text-white text-2xl md:text-3xl tracking-tight leading-tight">Three options + maintenance</div>
                <p className="text-white/50 text-sm mt-1">Customer receives Good / Better / Best pricing side-by-side. Rename any option to fit.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {options.map((opt, i) => (
                <OptionCard
                  key={i}
                  opt={opt}
                  index={i}
                  onChange={(next) => updateOption(i, next)}
                  onRemove={() => removeOption(i)}
                  canRemove={options.length > 1}
                />
              ))}
            </div>

            {options.length < 5 && (
              <button
                onClick={addOption}
                className="mt-4 inline-flex items-center gap-2 font-mono-accent text-[10px] tracking-[0.25em] uppercase text-[color:var(--primary)] hover:text-white transition-colors"
                data-testid="add-option"
              >
                <Plus size={12} /> Add another option
              </button>
            )}
          </div>

          {/* Maintenance Contract */}
          <div className="border border-white/10 bg-[#0C1017] p-6 md:p-8" data-testid="maintenance-section">
            <div className="flex items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <Wrench size={16} className="text-amber-300" />
                <div>
                  <div className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-amber-300">Add-on</div>
                  <div className="font-display uppercase text-white text-xl md:text-2xl tracking-tight leading-tight">Maintenance Contract</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-white/60">{maintenance.enabled ? "Included" : "Excluded"}</span>
                <Switch
                  checked={maintenance.enabled}
                  onCheckedChange={(v) => setMaintenance((m) => ({ ...m, enabled: v }))}
                  data-testid="maintenance-toggle"
                />
              </div>
            </div>

            <div className={`grid md:grid-cols-2 gap-6 transition-opacity ${maintenance.enabled ? "opacity-100" : "opacity-40 pointer-events-none"}`}>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className={labelClass}>Plan name</Label>
                  <Input
                    value={maintenance.name}
                    onChange={(e) => setMaintenance((m) => ({ ...m, name: e.target.value }))}
                    className={fieldClass + " h-10"}
                    data-testid="maintenance-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className={labelClass}>Visits per year</Label>
                    <Input
                      type="number" min="1" step="1"
                      value={maintenance.visits_per_year}
                      onChange={(e) => setMaintenance((m) => ({ ...m, visits_per_year: e.target.value }))}
                      className={fieldClass + " h-10"}
                      data-testid="maintenance-visits"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className={labelClass}>Price per year ($)</Label>
                    <Input
                      type="number" min="0" step="0.01"
                      value={maintenance.price_per_year}
                      onChange={(e) => setMaintenance((m) => ({ ...m, price_per_year: e.target.value }))}
                      className={fieldClass + " h-10"}
                      data-testid="maintenance-price"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className={labelClass}>What's included</Label>
                  <Textarea
                    value={maintenance.description || ""}
                    onChange={(e) => setMaintenance((m) => ({ ...m, description: e.target.value }))}
                    rows={3}
                    className={fieldClass}
                    data-testid="maintenance-description"
                  />
                </div>
              </div>
              <div className="border border-amber-300/30 bg-amber-300/[0.03] p-5">
                <div className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-amber-300 mb-3">Summary</div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-white/60"><span>Visits / yr</span><span className="text-white">{maintenance.visits_per_year || 0}</span></div>
                  <div className="flex justify-between text-white/60"><span>Per visit</span><span className="text-white">${perVisit.toFixed(2)}</span></div>
                </div>
                <div className="mt-4 pt-4 border-t border-white/10 flex items-baseline justify-between">
                  <span className="font-mono-accent text-[10px] tracking-[0.28em] uppercase text-white/60">Per year</span>
                  <span className="font-display uppercase text-3xl text-amber-300 tracking-tight" data-testid="maintenance-total">${Number(maintenance.price_per_year || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Personal message + send */}
          <div className="border border-white/10 bg-[#0C1017] p-6 md:p-8">
            <div className="grid md:grid-cols-[1fr_180px] gap-6">
              <div className="space-y-2">
                <Label className={labelClass}>Personal message (top of email)</Label>
                <Textarea
                  value={msg}
                  onChange={(e) => setMsg(e.target.value)}
                  rows={4}
                  className={fieldClass}
                  data-testid="personal-message"
                />
              </div>
              <div className="space-y-2">
                <Label className={labelClass}>Valid for (days)</Label>
                <Input
                  type="number" min="1"
                  value={validDays}
                  onChange={(e) => setValidDays(e.target.value)}
                  className={fieldClass + " h-10"}
                  data-testid="valid-days"
                />
              </div>
            </div>
            <div className="mt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="text-white/50 text-xs">
                Sending to <span className="text-white">{data.email}</span> — the customer receives all {options.length} option{options.length > 1 ? "s" : ""}{maintenance.enabled ? " + the maintenance contract" : ""} in a single professional email.
              </div>
              <button
                onClick={onSend}
                disabled={sending}
                data-testid="send-estimate"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[color:var(--primary)] text-black font-mono-accent text-[11px] tracking-[0.25em] uppercase hover:bg-white transition-colors btn-sharp disabled:opacity-60"
              >
                {sending ? <><Loader2 className="animate-spin" size={14} /> Sending</> : <>Send Estimate <Send size={12} /></>}
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-6 cursor-pointer" onClick={() => setLightbox(null)}>
          <img src={absoluteUrl(lightbox)} alt="attachment full" className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </AdminLayout>
  );
}
